import type {OnboardingGuidance as OnboardingGuidanceData} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {OnboardingGuidanceState} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {SoftNavOnlyLink as ReactLink} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  AppsIcon,
  ArrowLeftIcon,
  CommentDiscussionIcon,
  FileIcon,
  InfoIcon,
  IssueClosedIcon,
  PeopleIcon,
  ProjectIcon,
  ShieldIcon,
} from '@primer/octicons-react'
import {Button, Link as PrimerLink, Stack} from '@primer/react'
import {Card} from '@primer/react/experimental'
import {useCallback} from 'react'
import {useCreateDemoRepository} from '../hooks/use-create-demo-repository'

export interface OnboardingGuidanceProps {
  /** Server-authored onboarding state and destinations. */
  guidance: OnboardingGuidanceData
}

/**
 * Renders repository onboarding guidance inside a Primer Card.
 */
export function OnboardingGuidance({guidance}: OnboardingGuidanceProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const createDemoRepository = useCreateDemoRepository(guidance.nextTask?.createDemoRepositoryPath ?? '')
  const track = useCallback(
    (action: string, label: string) => {
      sendAnalyticsEvent('analytics.click', undefined, {category: 'ACTIONS_WORKFLOWS', action, label})
    },
    [sendAnalyticsEvent],
  )

  if (guidance.state === OnboardingGuidanceState.TIP) {
    return (
      <Card as="section" className="mb-4 ">
        <Card.Icon icon={InfoIcon} />
        <Card.Heading as="h2">Run a continuous integration test</Card.Heading>
        <Card.Description>
          This is your repository’s &quot;Actions&quot; tab, where you can find the jobs most recently run by your
          workflows. Your demo-repository includes the{' '}
          <PrimerLink inline href="https://github.com/marketplace/actions/proof-html" target="_blank">
            Proof HTML
          </PrimerLink>{' '}
          workflow, which allows developers to continuously integrate new code while automatically checking for invalid
          HTML.
          <br />
          <br />
          The <strong>proof-html.yml</strong> workflow in your repository is currently configured to run automatically
          whenever someone pushes code, or to run manually; which you can do from this page. Just select{' '}
          <strong>Run workflow</strong> below to see it in action.
        </Card.Description>
        <Card.Metadata>
          <Button as={ReactLink} to={guidance.tasksPath} variant="link" size="small" leadingVisual={ArrowLeftIcon}>
            Back to onboarding
          </Button>
          <Button
            as="a"
            href={guidance.continuousIntegrationDocsUrl}
            target="_blank"
            size="small"
            leadingVisual={FileIcon}
          >
            Continuous integration docs
          </Button>
        </Card.Metadata>
      </Card>
    )
  }

  if (guidance.state === OnboardingGuidanceState.COMPLETED_WITH_NEXT_TASK && guidance.nextTask) {
    const nextTask = guidance.nextTask
    const createsDemoRepository = Boolean(nextTask.createDemoRepositoryPath)

    return (
      <Card as="section" className="mb-4">
        <Card.Heading as="h2">You completed a task!</Card.Heading>
        <Card.Description>
          <Stack as="span" direction="horizontal" gap="condensed" align="center">
            <IssueClosedIcon aria-hidden="true" className="color-fg-success" />
            <span className="color-fg-muted">{guidance.completedTaskTitle}</span>
          </Stack>
        </Card.Description>
        <Card.Metadata>
          {createsDemoRepository ? (
            <Button
              variant="primary"
              loading={createDemoRepository.isPending}
              loadingAnnouncement="Creating demo repository"
              onClick={() => {
                track('onboarding.create_demo_repository', `ref_cta:${nextTask.title};task_key:${nextTask.taskKey}`)
                createDemoRepository.mutate()
              }}
            >
              Up next: {nextTask.title}
            </Button>
          ) : (
            <Button
              as="a"
              variant="primary"
              href={nextTask.taskPath}
              onClick={() => track('onboarding.next_task', `ref_cta:${nextTask.title};task_key:${nextTask.taskKey}`)}
            >
              Up next: {nextTask.title}
            </Button>
          )}
          <Button as="a" href={guidance.tasksPath}>
            See all tasks
          </Button>
        </Card.Metadata>
      </Card>
    )
  }

  if (guidance.state === OnboardingGuidanceState.ALL_TASKS_COMPLETED) {
    const showTrialActions = Boolean(guidance.upgradePath || guidance.contactPath)

    return (
      <Card as="section" padding="none" className="mb-4">
        <Stack direction="horizontal" align="stretch">
          <Stack.Item grow>
            <Stack padding="spacious">
              <Card.Heading as="h2">You’ve completed all your tasks!</Card.Heading>
              <Card.Description>Discover more GitHub features or upgrade your plan.</Card.Description>
              <Stack as="ul" gap="condensed" className="list-style-none">
                {FEATURE_LINKS.map(({icon: Icon, href, label}) => (
                  <Stack as="li" key={href} direction="horizontal" gap="condensed" align="center">
                    <Icon size={24} />
                    <PrimerLink inline className="text-semibold" href={href}>
                      {label}
                    </PrimerLink>
                  </Stack>
                ))}
              </Stack>
              {showTrialActions ? (
                <Card.Metadata>
                  {guidance.upgradePath ? (
                    <Button as="a" size="small" href={guidance.upgradePath} variant="primary">
                      Upgrade to Enterprise
                    </Button>
                  ) : null}
                  {guidance.contactPath ? (
                    <Button as="a" size="small" href={guidance.contactPath}>
                      Talk to us
                    </Button>
                  ) : null}
                </Card.Metadata>
              ) : null}
            </Stack>
          </Stack.Item>
        </Stack>
      </Card>
    )
  }

  return null
}

const FEATURE_LINKS = [
  {
    label: 'Security',
    href: '/features#features-security',
    icon: ShieldIcon,
  },
  {
    label: 'Client apps',
    href: '/features#features-apps',
    icon: AppsIcon,
  },
  {
    label: 'Project management',
    href: '/features#features-project-management',
    icon: ProjectIcon,
  },
  {
    label: 'Team administration',
    href: '/features#features-team-administration',
    icon: PeopleIcon,
  },
  {
    label: 'Community',
    href: '/features#features-community',
    icon: CommentDiscussionIcon,
  },
] as const
